import React from 'react';
import { Button, Divider, Empty, message, Modal, Space, Table, Tag } from 'antd';
import { ExclamationCircleFilled, HomeOutlined, TeamOutlined } from '@ant-design/icons';

import PageApp from '../../../Page';
import DrawerRole from './DrawerRole';
import { drop, getAll, getCatalogo, getVistasCatalogo, insert, update } from '../../../../apis/RolesApi';

const { confirm } = Modal;

const RolesPage = () => {
  const [roles, setRoles] = React.useState([]);
  const [catalogo, setCatalogo] = React.useState({ grupos_permisos: [], niveles_acceso: [] });
  const [vistasCatalogo, setVistasCatalogo] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [openDrawer, setOpenDrawer] = React.useState(false);
  const [drawerMode, setDrawerMode] = React.useState('add');
  const [selectedRole, setSelectedRole] = React.useState(null);

  const routes = [
    {
      href: '',
      title: (
        <>
          <HomeOutlined />
          <span>Inicio</span>
        </>
      ),
    },
    {
      href: '',
      title: (
        <>
          <TeamOutlined />
          <span>Roles</span>
        </>
      ),
    },
  ];

  const refreshData = async () => {
    setLoading(true);
    try {
      const [rolesResponse, catalogoResponse, vistasResponse] = await Promise.all([
        getAll(),
        getCatalogo(),
        getVistasCatalogo(),
      ]);

      if (rolesResponse?.ok) {
        setRoles(rolesResponse.payload || []);
      } else {
        setRoles([]);
      }

      if (catalogoResponse?.ok) {
        setCatalogo(catalogoResponse.payload || { grupos_permisos: [], niveles_acceso: [] });
      }

      if (vistasResponse?.ok) {
        setVistasCatalogo(vistasResponse.payload || []);
      } else {
        setVistasCatalogo([]);
      }
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    refreshData();
  }, []);

  const openAddDrawer = () => {
    setDrawerMode('add');
    setSelectedRole({
      id: null,
      name: '',
      descripcion: '',
      nivel_acceso_maximo_id: undefined,
      permisos: [],
    });
    setOpenDrawer(true);
  };

  const openEditDrawer = (role) => {
    setDrawerMode('edit');
    setSelectedRole(role);
    setOpenDrawer(true);
  };

  const closeDrawer = () => {
    setOpenDrawer(false);
    setSelectedRole(null);
  };

  const onSaveRole = async (payload) => {
    if (!payload.name) {
      message.warning('El nombre del rol es obligatorio');
      return;
    }

    setSaving(true);
    try {
      const isAdminRole = Number(payload?.id) === 100;

      const allPermisoIds = (catalogo?.grupos_permisos || []).flatMap((grupo) =>
        (grupo?.permisos || []).map((permiso) => permiso.id)
      );

      const finalPayload = {
        ...payload,
        permiso_ids: isAdminRole ? Array.from(new Set(allPermisoIds)) : payload.permiso_ids,
      };

      let response;
      if (drawerMode === 'add') {
        response = await insert(finalPayload);
      } else {
        response = await update(payload.id, finalPayload);
      }

      if (response?.ok) {
        message.success('Rol guardado correctamente');
        closeDrawer();
        await refreshData();
      }
    } finally {
      setSaving(false);
    }
  };

  const onDelete = (role) => {
    confirm({
      title: `¿Eliminar rol ${role.name}?`,
      icon: <ExclamationCircleFilled />,
      content: 'No podrás recuperar su configuración.',
      onOk: async () => {
        const response = await drop(role.id);
        if (response?.ok) {
          message.success('Rol eliminado correctamente');
          await refreshData();
        }
      },
    });
  };

  const columns = [
    {
      title: 'Rol',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Descripcion',
      dataIndex: 'descripcion',
      key: 'descripcion',
      render: (value) => value || '-',
    },
    {
      title: 'Nivel Maximo',
      dataIndex: 'nivel_acceso_maximo',
      key: 'nivel_acceso_maximo',
      render: (nivel) => (nivel ? <Tag color='purple'>{nivel.nombre}</Tag> : <Tag>-</Tag>),
    },
    {
      title: 'Permisos',
      dataIndex: 'permisos',
      key: 'permisos',
      render: (permisos) => <Tag color='blue'>{(permisos || []).length}</Tag>,
    },
    {
      title: 'Usuarios Asignados',
      dataIndex: 'users_count',
      key: 'users_count',
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status) =>
        status === 'Activo' ? <Tag color='green'>Activo</Tag> : <Tag color='default'>{status}</Tag>,
    },
    {
      title: '',
      key: 'actions',
      align: 'right',
      render: (_, role) => (
        <Space>
          <Button onClick={() => openEditDrawer(role)}>Editar</Button>
          <Button danger onClick={() => onDelete(role)}>
            Eliminar
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <PageApp routes={routes}>
      <Button type='primary' onClick={openAddDrawer}>
        Agregar Rol
      </Button>

      <Divider />

      {roles.length === 0 && !loading ? (
        <Empty />
      ) : (
        <Table rowKey='id' dataSource={roles} columns={columns} loading={loading} />
      )}

      <DrawerRole
        open={openDrawer}
        mode={drawerMode}
        role={selectedRole}
        catalogo={catalogo}
        vistasCatalogo={vistasCatalogo}
        onClose={closeDrawer}
        onSave={onSaveRole}
        loading={saving}
      />
    </PageApp>
  );
};

export default RolesPage;
